import { useEffect, useState, type FormEvent } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { useAuth } from '../../state/auth';
import { useLanguage } from '../../i18n/LanguageProvider';
import './security-card.css';

export interface SecurityCardProps {
  className?: string;
  figmaNode?: string;
  onChangePassword?: (currentPassword: string, newPassword: string) => Promise<void>;
  socialAccounts?: string[];
}

const SERVICE_NAMES: Record<string, string> = { google: 'Google', apple: 'Apple', yandex: 'Яндекс', vk: 'VK' };

export function SecurityCard({
  className = '',
  figmaNode = '2680:40911',
  onChangePassword,
  socialAccounts,
}: SecurityCardProps) {
  const { user, refresh } = useAuth();
  const { language } = useLanguage();
  const [servicesLoading, setServicesLoading] = useState(true);
  useEffect(() => {
    let active = true;
    refresh().finally(() => { if (active) setServicesLoading(false); });
    return () => { active = false; };
  }, [refresh]);
  const accounts = socialAccounts ?? (user?.linked_services ?? []).map((provider) => (
    provider === 'yandex' && language === 'en' ? 'Yandex' : SERVICE_NAMES[provider] ?? provider
  ));
  const [isOpen, setIsOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNewPasswords, setShowNewPasswords] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!currentPassword) {
      setError('Введите текущий пароль');
      return;
    }
    if (!newPassword) {
      setError('Введите новый пароль');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }
    if (newPassword === currentPassword) {
      setError('Новый пароль должен отличаться от текущего');
      return;
    }

    if (!onChangePassword) {
      setSuccess(true);
      setTimeout(() => setIsOpen(false), 1000);
      return;
    }

    setSubmitting(true);
    try {
      await onChangePassword(currentPassword, newPassword);
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => {
        setIsOpen(false);
        setSuccess(false);
      }, 1500);
    } catch (err: unknown) {
      let msg = 'Не удалось изменить пароль. Проверьте правильность введённых данных';
      if (err && typeof err === 'object') {
        if ('payload' in err && err.payload && typeof err.payload === 'object') {
          const payload = err.payload as Record<string, unknown>;
          if (Array.isArray(payload.current_password) && payload.current_password.length > 0) {
            msg = String(payload.current_password[0]);
          } else if (Array.isArray(payload.new_password) && payload.new_password.length > 0) {
            msg = String(payload.new_password[0]);
          } else if (Array.isArray(payload.non_field_errors) && payload.non_field_errors.length > 0) {
            msg = String(payload.non_field_errors[0]);
          } else if (typeof payload.detail === 'string') {
            msg = payload.detail;
          }
        } else if ('message' in err && typeof err.message === 'string' && !err.message.startsWith('HTTP 400')) {
          msg = err.message;
        }
      }
      setError(msg.replace(/\.+$/, ''));
    } finally {
      setSubmitting(false);
    }
  };

  const resetAndClose = () => {
    setIsOpen(false);
    setError('');
    setSuccess(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNewPasswords(false);
  };

  return (
    <>
      <section
        className={`lovo-security-card ${className}`}
        data-figma-node={figmaNode}
      >
        <h2>Безопасность</h2>
        <div className="lovo-security-card__password-row">
          <label>
            <span>Пароль</span>
            <input disabled readOnly type="password" value="••••••••" />
          </label>
          <button
            className="lovo-security-card__password-btn"
            type="button"
            onClick={() => setIsOpen(true)}
          >
            Сменить
          </button>
        </div>

        <div className="lovo-security-card__linked-section">
          <span>Привязанные аккаунты</span>
          <div className="lovo-security-card__linked-chips">
            {!servicesLoading && accounts.length === 0 ? <span data-i18n-skip>{language === 'en' ? 'No linked accounts' : 'Нет привязанных аккаунтов'}</span> : null}
            {servicesLoading ? <span data-i18n-skip>{language === 'en' ? 'Loading…' : 'Загружаем…'}</span> : accounts.map((account) => (
              <span className="lovo-security-card__chip" key={account}>
                {account}
              </span>
            ))}
          </div>
        </div>
      </section>

      {isOpen && (
        <div
          className="lovo-security-modal__backdrop"
          onClick={resetAndClose}
        >
          <div
            className="lovo-security-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="lovo-security-modal__header">
              <h3>Смена пароля</h3>
              <button
                className="lovo-security-modal__close"
                type="button"
                onClick={resetAndClose}
              >
                <X size={20} />
              </button>
            </div>

            {success && (
              <div className="lovo-security-modal__alert is-success">
                ✓ Пароль успешно изменён!
              </div>
            )}
            {error && (
              <div className="lovo-security-modal__alert is-error">
                {error}
              </div>
            )}

            <form onSubmit={(e) => void handleSubmit(e)}>
              <label>
                <span>Текущий пароль</span>
                <div className="lovo-security-modal__input-wrapper">
                  <input
                    placeholder="Введите пароль"
                    required
                    type={showCurrent ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                  />
                  {currentPassword.length > 0 && (
                    <button
                      className="lovo-security-modal__eye-btn"
                      tabIndex={-1}
                      type="button"
                      onClick={() => setShowCurrent(!showCurrent)}
                    >
                      {showCurrent ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  )}
                </div>
              </label>

              <label>
                <span>Новый пароль</span>
                <div className="lovo-security-modal__input-wrapper">
                  <input
                    placeholder="Придумайте пароль"
                    required
                    type={showNewPasswords ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                  {(newPassword.length > 0 || confirmPassword.length > 0) && (
                    <button
                      className="lovo-security-modal__eye-btn"
                      tabIndex={-1}
                      type="button"
                      onClick={() => setShowNewPasswords(!showNewPasswords)}
                    >
                      {showNewPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  )}
                </div>
                <small className="lovo-security-modal__hint">Минимум 8 символов, одна заглавная буква и одна цифра</small>
              </label>

              <label>
                <span>Повторите новый пароль</span>
                <div className="lovo-security-modal__input-wrapper">
                  <input
                    placeholder="Повторите пароль"
                    required
                    type={showNewPasswords ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  {(newPassword.length > 0 || confirmPassword.length > 0) && (
                    <button
                      className="lovo-security-modal__eye-btn"
                      tabIndex={-1}
                      type="button"
                      onClick={() => setShowNewPasswords(!showNewPasswords)}
                    >
                      {showNewPasswords ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  )}
                </div>
              </label>

              <div className="lovo-security-modal__actions">
                <button
                  className="lovo-security-modal__cancel"
                  type="button"
                  onClick={resetAndClose}
                >
                  Отмена
                </button>
                <button
                  className="lovo-security-modal__submit"
                  disabled={submitting}
                  type="submit"
                >
                  {submitting ? 'Сохранение...' : 'Сохранить'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
